<template>
    <simple-modal :show="showTicketSummaryModal" @close="handleCloseButtonClick" :show-title="false">
        <div v-if="selectedTicket">
            <div class="pt-2 flex justify-between items-center">
                <div class="flex items-center flex-wrap gap-x-2">
                    <div class="flex items-center whitespace-nowrap">
                        <div v-if="selectedTicket.status ==='open'" class="ml-2 flex items-center justify-center">
                            <span class="w-2 h-2 rounded-full"
                                  :class="getTicketReferenceColorStyles(selectedTicket, 'bg')">
                            </span>
                        </div>

                        <span v-else class="px-1 bg-gray-100 text-gray-400 rounded text-2xs font-medium">closed</span>
                    </div>

                    <div class="block font-medium text-base" :class="getTicketReferenceColorStyles(selectedTicket, 'text')">
                        {{ selectedTicket.ticket_reference_number }}
                    </div>

                    <div class="font-medium text-base ml-2" :class="getTicketReferenceColorStyles(selectedTicket, 'text')">
                        <a :href="`/assets/${selectedTicket.asset_id}`" target="_blank" title="Open Asset" class="flex hover:text-indigo-800 transition duration-300">
                            {{ selectedTicket.asset_name }}
                            <icon-arrow-to-top-right class="ml-0.5 w-4 h-4"></icon-arrow-to-top-right>
                        </a>
                    </div>
                </div>

                <button class="text-gray-800 font-semibold outline-none hover:text-indigo-600 transition duration-300"
                        @click="handleCloseButtonClick" type="button">&#10005;
                </button>
            </div>

            <div class="mt-2">
                <dl class="grid grid-cols-1 sm:grid-cols-2 gap-1">
                    <div class="p-2 sm:col-span-1">
                        <dt class="text-sm font-medium leading-4 text-gray-900">Service Provider</dt>
                        <dd class="mt-1 text-sm leading-4 text-gray-700 sm:mt-2">
                            <div class="whitespace-nowrap">
                                <div v-if="selectedTicket.service_provider">{{ selectedTicket.service_provider.name }}
                                    <div class="block text-indigo-600 font-medium whitespace-nowrap">
                                        {{ selectedTicket.service_provider.type }}
                                    </div>
                                </div>
                                <div v-else>Not Assigned</div>

                            </div>
                        </dd>
                    </div>

                    <div class="p-2 sm:col-span-1" v-if="selectedTicket.phase">
                        <dt class="text-sm font-medium leading-4 text-gray-900">Current Phase</dt>
                        <dd class="mt-1 text-sm leading-4 text-gray-700 sm:mt-2">
                            <badge class="text-yellow-600 bg-yellow-100" text-size="text-sm">
                                {{ selectedTicket.phase.name }}
                            </badge>
                        </dd>
                    </div>

                    <div class="p-2 sm:col-span-1" v-if="selectedTicket.phase">
                        <dt class="text-sm font-medium leading-4 text-gray-900">Last Phase Update</dt>
                        <dd class="mt-1 text-sm leading-4 text-gray-700 sm:mt-2">
                            <span class="">
                                {{ selectedTicket.phase.last_updated_at }}
                            </span>
                        </dd>
                    </div>

                    <div class="p-2 sm:col-span-1">
                        <dt class="text-sm font-medium leading-4 text-gray-900">Logged at</dt>
                        <dd class="mt-1 text-sm leading-4 text-gray-700 sm:mt-2">{{ selectedTicket.logged_at }}</dd>
                    </div>

                    <div class="p-2 sm:col-span-1">
                        <dt class="text-sm font-medium leading-4 text-gray-900">Resolved at</dt>
                        <dd class="mt-1 text-sm leading-4 text-gray-700 sm:mt-2">{{
                                selectedTicket.resolved_at ? selectedTicket.resolved_at : "Not Resolved Yet"
                            }}
                        </dd>
                    </div>

                    <div class="p-2 sm:col-span-1 lg:hidden" v-if="selectedTicket.assigned_to && selectedTicket.assigned_to.name">
                        <dt class="text-sm font-medium leading-4 text-gray-900 whitespace-nowrap">Assigned To</dt>
                        <dd class="mt-1 text-sm leading-4 text-gray-700 sm:mt-2">{{ selectedTicket.assigned_to.name }}</dd>
                    </div>

                    <div class="p-2 sm:col-span-1"
                         v-if="selectedTicket.next_todo && selectedTicket.next_todo.latest_reminder && selectedTicket.next_todo.latest_reminder.reminder_date">
                        <dt class="text-sm font-medium leading-4 text-gray-900 whitespace-nowrap">Next Reminder</dt>
                        <dd class="mt-1 text-sm leading-4 text-gray-700 sm:mt-2">{{ selectedTicket.next_todo.latest_reminder.reminder_date | datetime }}</dd>
                    </div>

                    <div class="p-2 sm:col-span-2" v-if="selectedTicket.latest_user_activity && selectedTicket.latest_user_activity.activity">
                        <dt class="text-sm font-medium leading-4 text-gray-900 whitespace-nowrap">Last Activity</dt>
                        <dd class="mt-1 text-sm leading-4 sm:mt-2">
                            <div class="">{{ selectedTicket.latest_user_activity.activity }}
                                <span class="text-gray-400" v-if="selectedTicket.latest_user_activity.notes">
                                    &middot; {{ selectedTicket.latest_user_activity.notes }}
                                </span>
                            </div>
                            <div class="mt-2">{{ selectedTicket.latest_user_activity.created_at }}</div>
                        </dd>
                    </div>

                    <div class="p-2 sm:col-span-2">
                        <dt class="text-sm font-medium leading-4 text-gray-900">Full Ticket Description</dt>
                        <dd class="mt-1 text-sm leading-4 text-gray-700 sm:mt-2">
                            <div>{{ selectedTicket.description }}</div>
                        </dd>
                    </div>

                    <div v-if="selectedTicket.latest_transaction_request" class="p-2 sm:col-span-2">
                        <dt class="text-sm font-medium leading-4 text-gray-900">Latest Billing Request Details</dt>
                        <dd class="mt-1 text-sm leading-4 text-gray-700 sm:mt-2">
                            <badge :class="[getTransactionRequestStatusColor(selectedTicket.latest_transaction_request.status), 'mt-1 flex flex-col']"
                                   v-if="selectedTicket.latest_transaction_request">
                                <span class="w-full items-start">Billing Request {{ selectedTicket.latest_transaction_request.status }}</span>
                                <span v-if="selectedTicket.latest_transaction_request.status === 'rejected'">
                                    {{ selectedTicket.latest_transaction_request.rejection_message }}
                                </span>
                            </badge>
                        </dd>
                    </div>
                </dl>
            </div>
        </div>

        <div slot="footer" class="flex justify-end -mt-6 px-6 py-4 w-full" v-if="selectedTicket">
            <a :href="`/tickets/${selectedTicket.id}`" class="text-indigo-600 text-sm font-medium">
                Go to ticket
            </a>
        </div>
    </simple-modal>
</template>


<script>

import TicketStyles from "../../mixins/ticket-styles";

export default {
    name: "TicketSummaryModal",
    mixins: [TicketStyles],
    props: ["selectedTicket", "showTicketSummaryModal"],
    methods: {
        handleCloseButtonClick () {
            this.$emit("close");
        }
    }
};
</script>
